import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Upload, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { getCategories, getSubcategories, createSubcategory, updateSubcategory, deleteSubcategory, uploadSubcategoryIcon } from '../../services/api';
import { toast } from 'sonner';

const SubcategoriesAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', category_id: '', order: 0, is_active: true });
  const [iconFile, setIconFile] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCategories();
        setCategories(res.data);
        if (res.data.length > 0) {
          setSelectedCategory(res.data[0].id);
        }
      } catch { toast.error('Failed to load categories'); }
      finally { setLoading(false); }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) fetchSubs();
  }, [selectedCategory]);

  const fetchSubs = async () => {
    try {
      const res = await getSubcategories(selectedCategory);
      setSubcategories(res.data || []);
    } catch { setSubcategories([]); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', slug: '', description: '', category_id: selectedCategory, order: 0, is_active: true });
    setIconFile(null);
    setDialogOpen(true);
  };

  const openEdit = (sub) => {
    setEditing(sub);
    setForm({
      name: sub.name || '', slug: sub.slug || '', description: sub.description || '',
      category_id: sub.category_id || selectedCategory, order: sub.order || 0, is_active: sub.is_active !== false
    });
    setIconFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = form.slug || form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    try {
      let iconUrl = editing?.icon_url || null;
      if (iconFile) {
        const uploadRes = await uploadSubcategoryIcon(iconFile);
        iconUrl = uploadRes.data.url;
      }
      const payload = { ...form, slug, icon_url: iconUrl };
      if (editing) {
        await updateSubcategory(editing.id, payload);
        toast.success('Subcategory updated');
      } else {
        await createSubcategory(payload);
        toast.success('Subcategory created');
      }
      setDialogOpen(false);
      fetchSubs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (sub) => {
    if (!window.confirm(`Delete subcategory "${sub.name}"?`)) return;
    try {
      await deleteSubcategory(sub.id);
      toast.success('Deleted');
      fetchSubs();
    } catch { toast.error('Delete failed'); }
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '';

  const filteredSubs = subcategories.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="subcategories-admin">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Subcategories</h1>
          <p className="text-sm text-gray-500">{subcategories.length} subcategories in {getCategoryName(selectedCategory)}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#2E3A6F]" data-testid="add-subcategory-btn" disabled={!selectedCategory}>
          <Plus size={16} className="mr-2" /> Add Subcategory
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <Label className="text-xs text-gray-500 mb-1 block">Filter by Category</Label>
          <select
            className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            data-testid="subcategory-category-filter"
          >
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="flex-1">
          <Label className="text-xs text-gray-500 mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input placeholder="Search subcategories..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
        </div>
      </div>

      {/* Subcategories Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Card key={i} className="animate-pulse"><CardContent className="p-4 h-24" /></Card>)}
        </div>
      ) : filteredSubs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center text-gray-500">
            {search ? 'No subcategories match your search.' : 'No subcategories yet. Click "Add Subcategory" to create one.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSubs.map((sub) => (
            <Card key={sub.id} className="hover:shadow-md transition-shadow" data-testid={`subcategory-card-${sub.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-[#2E3A6F]/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {sub.icon_url ? (
                      <img
                        src={sub.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${sub.icon_url}` : sub.icon_url}
                        alt={sub.name}
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#2E3A6F]/30" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-sm text-gray-900 truncate">{sub.name}</h3>
                      {!sub.is_active && <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">Inactive</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Slug: {sub.slug}</p>
                    {sub.description && <p className="text-xs text-gray-600 mt-1 line-clamp-2">{sub.description}</p>}
                    <p className="text-xs text-gray-400 mt-1">Order: {sub.order || 0}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(sub)} className="flex-1 text-xs">
                    <Pencil size={12} className="mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(sub)} className="text-red-600 hover:bg-red-50 text-xs">
                    <Trash2 size={12} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Subcategory</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Parent Category</Label>
              <select
                className="w-full h-9 rounded-md border border-input px-3 text-sm"
                value={form.category_id}
                onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                required
                data-testid="subcategory-parent-select"
              >
                <option value="">Select Category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="subcategory-name-input" /></div>
            <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div>
              <Label>Icon (64x64 px, PNG recommended)</Label>
              <div className="flex items-center gap-3 mt-1">
                {(editing?.icon_url || iconFile) && (
                  <div className="w-12 h-12 rounded-lg border bg-gray-50 flex items-center justify-center overflow-hidden">
                    {iconFile ? (
                      <img src={URL.createObjectURL(iconFile)} alt="Preview" className="w-10 h-10 object-contain" />
                    ) : editing?.icon_url ? (
                      <img
                        src={editing.icon_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${editing.icon_url}` : editing.icon_url}
                        alt="Current"
                        className="w-10 h-10 object-contain"
                      />
                    ) : null}
                  </div>
                )}
                <label className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-dashed border-gray-300 hover:border-[#2E3A6F] transition-colors text-sm text-gray-600">
                    <Upload size={14} />
                    {iconFile ? iconFile.name : 'Upload custom icon'}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setIconFile(e.target.files[0])} data-testid="subcategory-icon-upload" />
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Order</Label><Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label>Active</Label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="subcategory-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubcategoriesAdmin;
